import React from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import { segmentEventsNames, trackSegmentEvent } from '@app/utils/analytics'
import { getSessionIdSelector } from '@app/store/session'
import { Button } from '@ui/blocks/Button'
import { Link } from '@ui/blocks/Link'
import { H4 } from '@ui/blocks/Heading'
import { Image } from '@ui/blocks/Image'
import { Lottie, LottieOptions } from '@ui/blocks/Lottie'
import abaImage from '@app/ui/assets/images/header/aba-menu-pic.png'
import diagnosisImage from '@app/ui/assets/images/header/diagnosis-menu-pic.jpg'
import parentPortalBg from '@app/ui/assets/images/header/menu-parent-portal-bg.svg'
import { ROUTES } from '@app/constants'
import { navigateTo } from '@utils/navigateTo'
import animationData from '@app/ui/assets/animations/submenu-animation/data'

import SubMenuContainer from './SubMenuContainer'
import { PARENTS_URL } from './data'

export interface SubListLinkProps {
  isOpen: boolean
  mouseLeave: () => void
  mouseEnter: () => void
}

const SubSectionWrapper = styled.div`
  display: flex;
  padding: 1rem;
  color: ${({ theme }) => theme.global.colors.genoa};
  min-width: 96.5rem;
`
const StyledButton = styled(Button)`
  line-height: 4.2rem;
  z-index: ${({ theme }) => theme.global.depth.elevated};
`

const SubSectionItem = styled.div`
  flex: 0 0 30%;

  &:last-child {
    flex: 0 0 40%;
  }

  nav {
    display: flex;
    flex-direction: column;
    a {
      font-style: normal;
      font-weight: normal;
      font-size: 1.5rem;
      line-height: 2.6rem;
      color: ${({ theme }) => theme.global.colors.genoa};
      margin-bottom: 1.2rem;
    }
  }
`

const SubMenuLogin = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 38.9rem;
  height: 42.9rem;
  background-color: ${({ theme }) => theme.global.colors.ecru};
  background-image: url(${parentPortalBg});
  background-position: right bottom;

  h2 {
    color: #195a5b;
    margin: 0;
    text-transform: capitalize;
  }

  a {
    text-transform: capitalize;
  }

  p {
    text-align: center;
    max-width: 25.2rem;
  }
`

const MenuHeading = styled.h2`
  font-family: 'Playfair Display', serif;
  font-style: normal;
  font-size: 2.8rem;
  line-height: 3.2rem;
  text-transform: capitalize;
  margin: 0 0 1.6rem 0;
  color: ${({ theme }) => theme.global.colors.genoa};
`
const StyledText = styled.p`
  margin: 0 0 1.9rem 0;
  color: ${({ theme }) => theme.global.colors.elemyGreen};
`

const SubTitle = styled.p`
  margin: 0 0 0.7rem 0;
  font-size: 1.5rem;
  line-height: 2.6rem;
  color: ${({ theme }) => theme.global.colors.elemyGreen};
`

const MenuImg = styled(Image)`
  margin-bottom: 4rem;
  display: block;
`

const ParentPortalTitle = styled(H4)`
  color: ${({ theme }) => theme.global.colors.elemyGreen};
  margin-bottom: 0.6rem;
  margin-top: 16rem;
`

const AnimationWrapper = styled.div`
  position: absolute;
  font-size: 0;
`

const SubSection = ({ isOpen, mouseLeave, mouseEnter }: SubListLinkProps) => {
  const sessionId = useSelector(getSessionIdSelector)

  const animationOptions: LottieOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData
  }

  const handleClickLogin = () => {
    if (sessionId) {
      trackSegmentEvent(sessionId, segmentEventsNames.PARENT360_LOG_IN)
    }

    navigateTo(PARENTS_URL)
  }

  return (
    <SubMenuContainer {...{ mouseEnter, mouseLeave, isOpen }} rightOffset={-40}>
      <SubSectionWrapper>
        <SubSectionItem>
          <Link to={ROUTES.abaTherapy}>
            <SubTitle>Have an autism diagnosis?</SubTitle>
            <MenuHeading>ABA Therapy</MenuHeading>
            <MenuImg width={253} height={212} src={abaImage} alt="Aba therapy" />
          </Link>

          <nav>
            <Link to={ROUTES.locations}>Our Locations</Link>
            <Link to={ROUTES.ourClinicians}>Our Providers</Link>
            <Link to={ROUTES.learningStudio}>Learning Studio</Link>
          </nav>
        </SubSectionItem>
        <SubSectionItem>
          <Link to={ROUTES.autismDiagnosis}>
            <SubTitle>Don’t have a diagnosis?</SubTitle>
            <MenuHeading>Autism Diagnosis</MenuHeading>
            <MenuImg width={253} height={212} src={diagnosisImage} alt="Aba therapy" />
          </Link>

          <nav>
            <Link to={ROUTES.abaCostCalculator}>ABA Cost Calculator</Link>
            <Link to={ROUTES.faq}>FAQ</Link>
          </nav>
        </SubSectionItem>
        <SubSectionItem>
          <SubMenuLogin>
            <AnimationWrapper>
              <Lottie options={animationOptions} />
            </AnimationWrapper>
            <ParentPortalTitle>Parent Portal</ParentPortalTitle>
            <StyledText>
              Portal for parents to keep track of their application status, submit documents, and more.
            </StyledText>
            <StyledButton onClick={handleClickLogin} variant="secondaryGreenText">
              Login
            </StyledButton>
          </SubMenuLogin>
        </SubSectionItem>
      </SubSectionWrapper>
    </SubMenuContainer>
  )
}

export default SubSection
